/**
 * @file useReadOnly.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-02
 * @modified 2026-09-02
 */

import { useEffect, useState } from 'react';

const read = () =>
  typeof document !== 'undefined' &&
  document.body?.getAttribute('data-readonly') === 'true';

/**
 * True when the signed-in account is view-only on the CURRENT screen.
 *
 * Reads the `data-readonly` attribute Dashboard maintains on <body> rather than
 * taking a user prop. That attribute is already the single source of truth for
 * this: Dashboard sets it from isReadOnly(user) and clears it on the Blend
 * Calculator, which is the one place a rep does real work. Deriving it here
 * again from a prop would be a second rule to keep in step with the first, and
 * shared components like RowActionsMenu are handed an action list with no user
 * anywhere in scope.
 *
 * Observed rather than read once: Dashboard sets the attribute in an effect, so
 * a component that mounted first would otherwise keep a stale `false` until
 * something else re-rendered it.
 */
export default function useReadOnly() {
  const [readOnly, setReadOnly] = useState(read);

  useEffect(() => {
    const sync = () => setReadOnly(read());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-readonly'] });
    return () => observer.disconnect();
  }, []);

  return readOnly;
}
